import React, { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import { TableKit } from '@tiptap/extension-table';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
}

const COLORS: { label: string; value: string | null; swatch: string }[] = [
  { label: 'Красный (важно)', value: '#C62828', swatch: '#C62828' },
  { label: 'Зелёный', value: '#2E7D32', swatch: '#2E7D32' },
  { label: 'Синий', value: '#1565C0', swatch: '#1565C0' },
  { label: 'Обычный', value: null, swatch: 'transparent' },
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      TableKit.configure({ table: { resizable: true } }),
    ],
    content: value || '<p></p>',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: 'kb-editor-area' } },
  });

  // Подгрузка контента при переключении темы.
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) editor.commands.setContent(value || '<p></p>', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const btn = (label: React.ReactNode, onClick: () => void, active = false, title?: string) => (
    <button
      type="button"
      className={`kb-tb-btn${active ? ' active' : ''}`}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
    >
      {label}
    </button>
  );

  const c = () => editor.chain().focus();

  return (
    <div className="kb-editor">
      <div className="kb-toolbar">
        {btn(<b>B</b>, () => c().toggleBold().run(), editor.isActive('bold'), 'Жирный')}
        {btn(<i>I</i>, () => c().toggleItalic().run(), editor.isActive('italic'), 'Курсив')}
        {btn('H2', () => c().toggleHeading({ level: 2 }).run(), editor.isActive('heading', { level: 2 }))}
        {btn('H3', () => c().toggleHeading({ level: 3 }).run(), editor.isActive('heading', { level: 3 }))}
        {btn('• Список', () => c().toggleBulletList().run(), editor.isActive('bulletList'))}
        {btn('1. Список', () => c().toggleOrderedList().run(), editor.isActive('orderedList'))}
        <span className="kb-tb-sep" />
        {COLORS.map(col => (
          <button
            key={col.label}
            type="button"
            className="kb-tb-color"
            title={col.label}
            onMouseDown={(e) => {
              e.preventDefault();
              col.value ? c().setColor(col.value).run() : c().unsetColor().run();
            }}
            style={{ background: col.swatch, border: col.value ? 'none' : '1px solid var(--border)' }}
          >
            {col.value ? '' : '×'}
          </button>
        ))}
        <span className="kb-tb-sep" />
        {btn('⊞ Таблица', () => c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(), false, 'Вставить таблицу')}
        {btn('+ стр', () => c().addRowAfter().run(), false, 'Строка ниже')}
        {btn('+ стлб', () => c().addColumnAfter().run(), false, 'Столбец справа')}
        {btn('− стр', () => c().deleteRow().run(), false, 'Удалить строку')}
        {btn('− стлб', () => c().deleteColumn().run(), false, 'Удалить столбец')}
        {btn('✕ табл', () => c().deleteTable().run(), false, 'Удалить таблицу')}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export { RichTextEditor };
